import React from "react";
import Button from "@/components/actions/Button";
import { createPortal } from "react-dom";

import classNames from "classnames";
import {
  ChevronDownIcon,
  SearchIcon,
  AdjustmentsIcon,
  FilterIcon,
  XIcon,
} from "@heroicons/react/outline";
import * as Text from "@/components/text";
import { Listbox, Transition } from "@headlessui/react";
import ClientOnlyPortal from "@/components/lib/ClientOnlyPortal";

export default function Filter({
  filters = [],
  align = "left",
  onChange = () => { },
  name = "Filtros",
  selected,
}) {
  const [searchList, setSearchList] = React.useState([]);
  const [searchText, setSearchText] = React.useState("");

  const handleSearch = (e) => {
    const value = e;
    if (value === "") {
      setSearchList(filters);
    } else {
      const filteredList = filters.filter((filter) =>
        filter.name.toLowerCase().includes(value.toLowerCase())
      );
      setSearchList(filteredList);
    }
  };

  React.useEffect(() => {
    handleSearch(searchText);
  }, [searchText]);

  return (
    <>
      <Listbox
        as="div"
        className=" relative "
        value={selected}
        onChange={onChange}
        multiple
      >
        {({ open }) => (
          <>
            <Listbox.Button
              as={Button}
              color={open ? "white-on-black-no-hover" : "primary-light"}
              label={name}
              className="relative px-4 font-semibold  "
              trailingIcon={<ChevronDownIcon width={32} height={32} />}
            ></Listbox.Button>

            <Transition
              enter="transition duration-100 ease-out"
              enterFrom="transform scale-95 opacity-0"
              enterTo="transform scale-100 opacity-100"
              leave="transition duration-75 ease-out"
              leaveFrom="transform scale-100 opacity-100"
              leaveTo="transform scale-95 opacity-0"
            >
              <Listbox.Options
                className={classNames(
                  align === "left" ? "left-0" : "right-0",
                  "top -10 absolute z-50  flex max-h-[400px] w-[254px]  flex-col overflow-y-auto border-[1px] border-black bg-white"
                )}
              >
                <div className="w-full border-b-2 py-2 ">
                  <div className="mx-2 flex items-center gap-4">
                    <SearchIcon
                      className="text-neutral-500"
                      width={20}
                      height={20}
                    />&nbsp;
                    <input
                      value={searchText}
                      className="w-full border-none outline-none ring-0 focus:ring-black"
                      placeholder="Buscar filtros"
                      type="text"
                      onChange={(e) => setSearchText(e.target.value)}
                    />
                  </div>
                </div>

                {searchList.length === 0 &&
                  filters.map((list) => (
                    <Listbox.Option
                      as="div"
                      className="py-2 sm:px-4"
                      key={list.name}
                      value={list.value}
                    >
                      {({ selected: s }) => (
                        <CheckBox
                          key={list.name}
                          name={list.name}
                          checked={s}
                        />
                      )}
                    </Listbox.Option>
                  ))}

                {searchList.length > 0 &&
                  searchList.map((list) => (
                    <Listbox.Option
                      as="div"
                      className="py-2 sm:px-4"
                      key={list.name}
                      value={list.value}
                    >
                      {({ selected: s }) => (
                        <CheckBox
                          key={list.name}
                          name={list.name}
                          checked={s}
                        />
                      )}
                    </Listbox.Option>
                  ))}

                <div className="sticky bottom-0 flex w-full flex-row items-center justify-between border-t-2 bg-white pt-2 sm:px-4 sm:pb-2">
                  <button
                    onClick={() => {
                      onChange([]);
                      setSearchText("");
                    }}
                    className="flex items-end justify-end border-b-2 border-neutral-400 font-semibold text-neutral-1000"
                  >
                    {`Limpiar`}
                  </button>
                </div>
              </Listbox.Options>
            </Transition>
          </>
        )}
      </Listbox>
    </>
  );
}

export const MobileFilter = ({
  themeFilters = [],
  selectedThemes,
  onThemeChange = () => { },
  categoriesFilters = [],
  onCategoryChange = () => { },
  selectedCategories,
}) => {
  const [isShowing, setIsShowing] = React.useState(false);
  const [categorySearchList, setCategorySearchList] = React.useState([]);
  const [themeSearchList, setThemeSearchList] = React.useState([]);
  const [searchText, setSearchText] = React.useState("");

  const handleSearch = (e) => {
    const value = e;
    if (value === "") {
      setThemeSearchList(themeFilters);
      setCategorySearchList(categoriesFilters);
    } else {
      setThemeSearchList(
        themeFilters.filter((filter) =>
          filter.name.toLowerCase().includes(value.toLowerCase())
        )
      );
      setCategorySearchList(
        categoriesFilters.filter((filter) =>
          filter.name.toLowerCase().includes(value.toLowerCase())
        )
      );
    }
  };

  React.useEffect(() => {
    handleSearch(searchText);
  }, [searchText]);

  return (
    <>
      <button
        onClick={() => setIsShowing(!isShowing)}
        className="z- left-0 flex h-full flex-col items-center justify-center active:bg-slate-100 "
      >
        <AdjustmentsIcon className=" h-6 w-6 text-neutral-900" />
      </button>

      <ClientOnlyPortal selector="#documentRoot">
        {isShowing && (
          <div
            onClick={() => setIsShowing(false)}
            className="fixed left-0 top-0 z-40 h-screen w-screen bg-neutral-900/50"
          ></div>
        )}
        <Transition as="div" show={isShowing}>
          <Transition.Child
            as="div"
            className="isShowing fixed bottom-0  z-50 h-screen-80 w-full overflow-scroll rounded-lg bg-white shadow-inner"
            enter="transition ease duration-300 transform"
            enterFrom="opacity-0 translate-y-12"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease duration-300 transform"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-12"
          >
            <div className="amb-container m-auto mt-5 space-y-5">
              <div className="flex items-center  ">
                <div className="flex h-full items-center gap-3">
                  <Text.Body as="p" size="xl">
                    &nbsp;
                    Filtros
                  </Text.Body>
                </div>
                <div className="flex-grow"></div>
                <div>
                  <XIcon
                    onClick={() => setIsShowing(!isShowing)}
                    className="h-6 w-6 text-neutral-900"
                  />
                </div>
              </div>
              <div className="w-full border-b-2 py-2 ">
                <div className="mx-2 flex items-center gap-4">
                  <SearchIcon
                    className="text-neutral-500"
                    width={20}
                    height={20}
                  />&nbsp;
                  <input
                    value={searchText}
                    className="w-full border-none outline-none ring-0 focus:ring-black"
                    placeholder="Buscar filtros"
                    type="text"
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div>
              <div className="mt-3 bg-neutral-50 ">
                <div className="amb-container py-3">Temáticas</div>
              </div>
            </div>
            <div className="amb-container">
              <Listbox
                as="div"
                className=" relative"
                value={selectedThemes}
                onChange={onThemeChange}
                multiple
              >
                {/* <Listbox.Button>abrir</Listbox.Button> */}
                <Listbox.Options
                  static
                  className={"grid grid-cols-2 md:grid-cols-3  "}
                >
                  {themeSearchList.length === 0 &&
                    themeFilters.map((list) => (
                      <Listbox.Option
                        as="div"
                        className="py-2 sm:px-4"
                        key={list.name}
                        value={list.value}
                      >
                        {({ selected: s }) => (
                          <CheckBox
                            key={list.name}
                            name={list.name}
                            checked={s}
                          />
                        )}
                      </Listbox.Option>
                    ))}

                  {themeSearchList.length > 0 &&
                    themeSearchList.map((list) => (
                      <Listbox.Option
                        as="div"
                        className="py-2 sm:px-4"
                        key={list.name}
                        value={list.value}
                      >
                        {({ selected: s }) => (
                          <CheckBox
                            key={list.name}
                            name={list.name}
                            checked={s}
                          />
                        )}
                      </Listbox.Option>
                    ))}
                </Listbox.Options>
              </Listbox>
            </div>

            <div>
              <div className="mt-3 bg-neutral-50 ">
                <div className="amb-container py-3">Categorías</div>
              </div>
            </div>
            <div className="amb-container">
              <Listbox
                as="div"
                className=" relative"
                value={selectedCategories}
                onChange={onCategoryChange}
                multiple
              >
                {/* <Listbox.Button>abrir</Listbox.Button> */}
                <Listbox.Options
                  static
                  className={"grid grid-cols-2 md:grid-cols-3  "}
                >
                  {categorySearchList.length === 0 &&
                    categoriesFilters.map((list) => (
                      <Listbox.Option
                        as="div"
                        className="py-2 sm:px-4"
                        key={list.name}
                        value={list.value}
                      >
                        {({ selected: s }) => (
                          <CheckBox
                            key={list.name}
                            name={list.name}
                            checked={s}
                          />
                        )}
                      </Listbox.Option>
                    ))}

                  {categorySearchList.length > 0 &&
                    categorySearchList.map((list) => (
                      <Listbox.Option
                        as="div"
                        className="py-2 sm:px-4"
                        key={list.name}
                        value={list.value}
                      >
                        {({ selected: s }) => (
                          <CheckBox
                            key={list.name}
                            name={list.name}
                            checked={s}
                          />
                        )}
                      </Listbox.Option>
                    ))}
                </Listbox.Options>
              </Listbox>
            </div>
          </Transition.Child>
        </Transition>
      </ClientOnlyPortal>
    </>
  );
};

export const CheckBox = ({ name, checked = false, ...rest }) => {
  return (
    <div {...rest} className="pointer-events-none relative flex items-center">
      <div className="flex h-6 items-center">
        <input
          id={name}
          checked={checked}
          readOnly
          aria-describedby="comments-description"
          name={name}
          type="checkbox"
          className="ring:black h-4 w-4 border-gray-300 text-black focus:ring-black"
        />
      </div>
      <div className="ml-3 flex-grow border-b border-b-slate-200 py-2 text-sm leading-6">
        <label htmlFor={name} className="font-medium text-gray-900">
          <Text.Body size="md">{name}</Text.Body>
        </label>
      </div>
    </div>
  );
};
